/**
 * Message Search Utility
 *
 * Client-side fuzzy search through chat messages using Fuse.js
 * Searches message content across all loaded sessions
 *
 * Security: 100% local, no network calls, HIPAA-safe
 */

import Fuse, { type IFuseOptions, type RangeTuple } from 'fuse.js';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:util:message-search');
import type { LocalChatSession } from '../stores/s3-chat-store';

/**
 * Search result with context
 */
export type MessageSearchResult = {
  // Session info
  sessionId: string;
  sessionTitle: string;
  sessionUpdatedAt: string;

  // Message info
  messageId: string;
  messageContent: string;
  messageTimestamp: string;
  messageSender: 'user' | 'ai';

  // Match info
  matchScore: number; // 0-1, lower is better match
  matchIndices: [number, number][]; // Character positions of matches
  snippet: string; // Context around the match
};

/**
 * Searchable message item (internal)
 */
type SearchableMessage = {
  sessionId: string;
  sessionTitle: string;
  sessionUpdatedAt: string;
  messageId: string;
  messageContent: string;
  messageTimestamp: string;
  messageSender: 'user' | 'ai';
};

/**
 * Fuse.js configuration for message search
 */
const FUSE_OPTIONS: IFuseOptions<SearchableMessage> = {
  keys: [
    {
      name: 'messageContent',
      weight: 1.0, // Primary search target
    },
    {
      name: 'sessionTitle',
      weight: 0.3, // Secondary: also search session titles
    },
  ],
  threshold: 0.4, // 0 = exact match, 1 = match anything (0.4 = fuzzy but not too loose)
  includeScore: true, // Return match scores
  includeMatches: true, // Return match positions for highlighting
  minMatchCharLength: 2, // Don't match single characters
  ignoreLocation: true, // Search entire string, not just beginning
  findAllMatches: true, // Find all matches, not just first
};

/**
 * Create a snippet around the first match
 */
const createSnippet = (
  content: string,
  matches: readonly RangeTuple[] | undefined,
  maxLength = 150
): string => {
  if (!matches || matches.length === 0) {
    // No matches, return start of content
    return content.slice(0, maxLength) + (content.length > maxLength ? '...' : '');
  }

  // Get first match position
  const firstMatch = matches[0];
  if (!firstMatch) {
    return content.slice(0, maxLength) + (content.length > maxLength ? '...' : '');
  }
  const [start] = firstMatch;

  // Calculate snippet boundaries
  const snippetStart = Math.max(0, start - 50);
  const snippetEnd = Math.min(content.length, start + 100);

  let snippet = content.slice(snippetStart, snippetEnd);

  // Add ellipsis if truncated
  if (snippetStart > 0) snippet = '...' + snippet;
  if (snippetEnd < content.length) snippet = snippet + '...';

  return snippet;
};

/**
 * Get search stats about loaded messages
 */
export const getSearchStats = (sessions: LocalChatSession[]) => {
  let loadedSessions = 0;
  let totalMessages = 0;

  sessions.forEach(session => {
    if (session.messages.length > 0) {
      loadedSessions++;
      totalMessages += session.messages.length;
    }
  });

  return {
    totalSessions: sessions.length,
    loadedSessions,
    totalMessages,
    unloadedSessions: sessions.length - loadedSessions,
  };
};

/**
 * Search messages across all loaded sessions
 *
 * @param sessions - All loaded chat sessions
 * @param query - Search query (min 2 characters)
 * @returns Array of search results, sorted by relevance
 */
export const searchMessages = (
  sessions: LocalChatSession[],
  query: string
): MessageSearchResult[] => {
  // Validate query
  const trimmedQuery = query.trim();
  if (trimmedQuery.length < 2) {
    return [];
  }

  // Flatten all messages from all sessions into searchable format
  const searchableMessages: SearchableMessage[] = [];

  sessions.forEach(session => {
    session.messages.forEach(message => {
      // Skip system messages (if any) and empty messages
      if (!message.sender || (message.sender !== 'user' && message.sender !== 'ai')) {
        logger.warn('Skipping message with invalid sender:', message.id, message.sender);
        return;
      }
      if (!message.content?.trim()) {
        return;
      }

      searchableMessages.push({
        sessionId: session.id,
        sessionTitle: session.title,
        sessionUpdatedAt: session.updatedAt,
        messageId: message.id,
        messageContent: message.content,
        messageTimestamp: message.timestamp,
        messageSender: message.sender,
      });
    });
  });

  // Create Fuse instance
  const fuse = new Fuse(searchableMessages, FUSE_OPTIONS);

  // Perform search
  const fuseResults = fuse.search(trimmedQuery);

  // Transform Fuse results to our format
  const results: MessageSearchResult[] = fuseResults.map(fuseResult => {
    const item = fuseResult.item;
    const score = fuseResult.score ?? 1;

    // Extract match indices for the message content
    const contentMatches = fuseResult.matches?.find(m => m.key === 'messageContent');
    const matchIndices = (contentMatches?.indices as [number, number][]) || [];

    return {
      sessionId: item.sessionId,
      sessionTitle: item.sessionTitle,
      sessionUpdatedAt: item.sessionUpdatedAt,
      messageId: item.messageId,
      messageContent: item.messageContent,
      messageTimestamp: item.messageTimestamp,
      messageSender: item.messageSender,
      matchScore: score,
      matchIndices,
      snippet: createSnippet(item.messageContent, contentMatches?.indices),
    };
  });

  // Sort by relevance (lower score = better match)
  return results.sort((a, b) => a.matchScore - b.matchScore);
};

/**
 * Highlight matching text in a string
 *
 * @param text - Original text
 * @param indices - Match positions from Fuse.js
 * @returns Text with <mark> tags around matches
 */
export const highlightMatches = (text: string, indices: [number, number][]): string => {
  if (!indices || indices.length === 0) {
    return text;
  }

  let result = '';
  let lastIndex = 0;

  // Sort indices by start position
  const sortedIndices = [...indices].sort((a, b) => a[0] - b[0]);

  sortedIndices.forEach(([start, end]) => {
    // Add text before match
    result += text.slice(lastIndex, start);

    // Add matched text with highlight
    result += '<mark class="bg-primary/20 text-foreground rounded px-0.5">';
    result += text.slice(start, end + 1);
    result += '</mark>';

    lastIndex = end + 1;
  });

  // Add remaining text
  result += text.slice(lastIndex);

  return result;
};
